import React, { createContext, useState, useContext, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {}
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Initialize from localStorage on mount
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });
  
  const auth = useAuth();
  
  // Load theme preference from Firebase when user changes
  useEffect(() => {
    const loadThemePreference = async () => {
      if (!auth || !auth.currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists() && userDoc.data().themeMode) {
          const firebaseTheme = userDoc.data().themeMode;
          setMode(firebaseTheme);
          localStorage.setItem('themeMode', firebaseTheme);
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };
    
    loadThemePreference();
  }, [auth]);
  
  // Save theme preference
  const saveThemePreference = async (newMode) => {
    // Always save to localStorage
    localStorage.setItem('themeMode', newMode);
    
    // Save to Firebase if user is logged in
    if (auth && auth.currentUser) {
      try {
        await setDoc(doc(db, 'users', auth.currentUser.uid), {
          themeMode: newMode
        }, { merge: true });
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
    }
  };
  
  // Toggle theme function
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    saveThemePreference(newMode);
  };
  
  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext); 